import React, { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import Modal from './Modal';
import { api, ApiError } from '../../lib/api';
import AccountPicker from './AccountPicker';
import { useAuth } from '../../context/AuthContext';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

const ROLES = [
  {
    value: 'client',
    label: 'Client',
    description: 'Scoped to a single account. Can submit tickets and edit requests for their own site.',
  },
  {
    value: 'ops',
    label: 'Operations',
    description: 'Operations access across all accounts. Full visibility except financial data.',
  },
  {
    value: 'nexsav_admin',
    label: 'NexSav Admin',
    description: 'Full access across all accounts, including financial data.',
  },
  {
    value: 'media',
    label: 'Media',
    description: 'Internal media staff (shooters). Own calendar + assigned shoots only; no account, financial, or admin access.',
  },
];


// Sprint K-3 — `lockedAccount` prop pins the account selector to a specific
// school when the form is opened from a per-account context (e.g., the
// UsersTab on AccountDetail). Pass `{ id, name }`. When set:
//   - Role tiles still render so admins can switch to ops/admin if needed,
//     but client-role users stay tied to this account.
//   - The AccountPicker is replaced with a static read-only label so the
//     lock is visually obvious.
//   - If the user picks a non-client role, the account auto-clears (existing
//     behavior) and the static label hides — that's intentional, since
//     ops/admin users aren't account-scoped.
export default function UserForm({ open, onClose, onSaved, initial, lockedAccount = null }) {
  // Sprint K-2 — `accounts` prop dropped. AccountPicker fetches its own
  // search results from /api/admin/accounts?q=... so the parent no longer
  // needs to pre-load the list (which was capped at 100).
  const editing = Boolean(initial?.id);
  const { user: actor } = useAuth();
  const canEditBillingAccess = actor?.role === 'nexsav_admin';
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'client',
    accountId: '',
    sendInvite: true,
    isActive: true,
    billingAccess: true,
    contentAccess: false,
  });
  const [fieldError, setFieldError] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Sprint K-3 — when opened from a per-account UsersTab the role is
    // forced to client and the accountId is forced to the locked account.
    // This guards against a stale initial value (e.g., editing a user whose
    // role was somehow changed elsewhere) from breaking the lock contract.
    const forceClient = !!lockedAccount;
    if (initial) {
      setForm({
        name: initial.name || '',
        email: initial.email || '',
        role: forceClient ? 'client' : (initial.role || 'client'),
        accountId: forceClient ? lockedAccount.id : (initial.accountId || ''),
        sendInvite: true,
        isActive: initial.isActive !== false,
        billingAccess: initial.billingAccess !== false,
        contentAccess: initial.contentAccess === true,
      });
    } else {
      setForm({
        name: '',
        email: '',
        role: 'client',
        accountId: forceClient ? lockedAccount.id : '',
        sendInvite: true,
        isActive: true,
        billingAccess: true,
        contentAccess: false,
      });
    }
    setFieldError({});
    setGeneralError('');
  }, [open, initial, lockedAccount]);

  const onChange = (field) => (e) => {
    const value =
      field === 'isActive' || field === 'billingAccess' || field === 'contentAccess' || field === 'sendInvite'
        ? e.target.checked
        : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && value !== 'client') next.accountId = '';
      return next;
    });
    setFieldError((prev) => ({ ...prev, [field]: undefined }));
    setGeneralError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFieldError({});
    setGeneralError('');

    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      role: form.role,
      accountId: form.role === 'client' ? form.accountId || null : null,
    };
    // No password: the server creates the account "invited" and emails a
    // single-use link to choose one (or hands the link back when sendInvite is off).
    if (!editing) payload.sendInvite = !!form.sendInvite;
    if (editing) payload.isActive = form.isActive;
    // Only include billingAccess when an admin is editing/creating a
    // client. Backend rejects with 403 if a non-admin sends this field,
    // and the column is unused for staff roles, so we omit it otherwise.
    if (canEditBillingAccess && form.role === 'client') {
      payload.billingAccess = !!form.billingAccess;
      payload.contentAccess = !!form.contentAccess;
    }

    try {
      let result;
      if (editing) {
        result = await api.put(`/api/admin/users/${initial.id}`, payload);
      } else {
        result = await api.post('/api/admin/users', payload);
      }
      setSaving(false);
      onSaved?.(result.user, !editing ? result.invite || null : null);
      onClose?.();
    } catch (err) {
      setSaving(false);
      if (err instanceof ApiError) {
        if (err.data?.field) {
          setFieldError({ [err.data.field]: err.message });
        } else {
          setGeneralError(err.message || 'Failed to save user');
        }
      } else {
        setGeneralError('Network error, please try again.');
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={editing ? 'Edit user' : 'New user'}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="user-form"
            disabled={saving}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                Saving…
              </span>
            ) : (
              editing ? 'Save changes' : 'Create user'
            )}
          </button>
        </div>
      }
    >
      <form id="user-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {generalError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {generalError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClasses}>Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={onChange('name')}
              className={inputClasses}
              placeholder="Jane Principal"
              autoFocus
              required
            />
            {fieldError.name && <p className="mt-1 text-[11px] text-red-400">{fieldError.name}</p>}
          </div>
          <div>
            <label className={labelClasses}>Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={onChange('email')}
              className={inputClasses}
              placeholder="jane@ps170x.org"
              required
            />
            {fieldError.email && <p className="mt-1 text-[11px] text-red-400">{fieldError.email}</p>}
          </div>
        </div>

        <div>
          <label className={labelClasses}>Role *</label>
          {lockedAccount ? (
            // Sprint K-3 — per-account UsersTab context. Only client role
            // makes sense here (ops/admin aren't account-scoped). Hide the
            // tile picker entirely and surface the implicit role as a
            // static label to match the locked-account UI affordance.
            <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white flex items-center justify-between gap-2">
              <span>Client</span>
              <span className="text-[10px] uppercase tracking-wider text-gray-500">
                Account-scoped portal user
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ROLES.map((r) => {
                const selected = form.role === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onChange('role')({ target: { value: r.value } })}
                    className={`text-left p-3.5 rounded-lg border transition-all flex items-start gap-2.5 ${
                      selected
                        ? 'bg-[#B0FC38]/[0.1] border-[#B0FC38]/40 ring-1 ring-[#B0FC38]/30'
                        : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.03]'
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${
                        selected ? 'border-[#B0FC38] bg-[#B0FC38]' : 'border-white/20'
                      }`}
                    >
                      {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium ${selected ? 'text-white' : 'text-gray-300'}`}>
                        {r.label}
                      </span>
                      <span className="block text-[11px] text-gray-500 mt-0.5 leading-relaxed">{r.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {fieldError.role && <p className="mt-1 text-[11px] text-red-400">{fieldError.role}</p>}
        </div>

        {form.role === 'client' && (
          <div>
            <label className={labelClasses}>Account *</label>
            {lockedAccount && form.accountId === lockedAccount.id ? (
              // Sprint K-3 — opened from a per-account context (UsersTab on
              // AccountDetail). Render a static label so the lock is obvious.
              // Switching role to ops/admin clears accountId and unmounts
              // this branch, which is the intended escape hatch.
              <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white flex items-center justify-between gap-2">
                <span>{lockedAccount.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">Locked</span>
              </div>
            ) : (
              <AccountPicker
                value={form.accountId}
                onChange={(id) => onChange('accountId')({ target: { value: id } })}
                required
                ariaInvalid={!!fieldError.accountId}
              />
            )}
            {fieldError.accountId && <p className="mt-1 text-[11px] text-red-400">{fieldError.accountId}</p>}
          </div>
        )}

        {!editing && (
          <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] cursor-pointer" data-testid="send-invite-option">
            <input
              type="checkbox"
              checked={form.sendInvite}
              onChange={onChange('sendInvite')}
              className="mt-0.5 accent-[#B0FC38]"
            />
            <span className="text-[12px] leading-relaxed">
              <span className="flex items-center gap-1.5 text-gray-200 font-medium"><Send className="w-3.5 h-3.5" aria-hidden="true" />Email an invite to set their password</span>
              <span className="block text-gray-500 mt-0.5">
                {form.sendInvite
                  ? 'They get a link that works once and expires in 3 days. Nobody else, including you, ever sees their password.'
                  : 'You’ll get the invite link to pass on yourself, for example by text. It still works once and expires in 3 days.'}
              </span>
            </span>
          </label>
        )}

        {editing && (
          <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={onChange('isActive')}
              className="mt-0.5 w-4 h-4 rounded accent-[#B0FC38]"
            />
            <div>
              <div className="text-sm text-white">Account active</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                Disabled users can't log in and existing sessions are revoked immediately.
              </div>
            </div>
          </label>
        )}

        {form.role === 'client' && canEditBillingAccess && (
          <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.billingAccess}
              onChange={onChange('billingAccess')}
              className="mt-0.5 w-4 h-4 rounded accent-[#B0FC38]"
            />
            <div>
              <div className="text-sm text-white">Billing access</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                Allows this user to see invoices, payments, and signature requests on their portal.
                Leave unchecked for staff who shouldn't see financial data.
              </div>
            </div>
          </label>
        )}

        {form.role === 'client' && canEditBillingAccess && (
          <label className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.05] cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.contentAccess}
              onChange={onChange('contentAccess')}
              className="mt-0.5 w-4 h-4 rounded accent-[#B0FC38]"
            />
            <div>
              <div className="text-sm text-white">Content oversight</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                Lets this user see every edit request and support ticket submitted across their
                organization (with submitter names) and edit or delete them until resolved, e.g. a
                director reviewing staff submissions. Users without it only see their own.
              </div>
            </div>
          </label>
        )}
      </form>
    </Modal>
  );
}
